import { Helmet } from "react-helmet-async";

const BUSINESS = {
  name: "Nexora Learning & Development Centre",
  legalName: "e2 Young Engineers Richmond",
  streetAddress: "220-11780 Hammersmith Way",
  locality: "Richmond",
  region: "BC",
  country: "CA",
  postalCode: "V7A 5E9",
  telephone: "+1-604-655-5306",
  email: "admissions@nexoracentre.ca",
  areaServed: ["Richmond", "Delta", "Tsawwassen", "Burnaby", "Vancouver"],
};

// JSON-LD safe serializer: escape characters that could break out of the
// <script> tag, so CMS-entered text can never inject markup into the head.
function safeJsonLd(obj) {
  return JSON.stringify(obj)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026");
}

function localBusinessSchema(siteUrl, stats) {
  const base = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "EducationalOrganization"],
    "@id": `${siteUrl}#organization`,
    name: BUSINESS.name,
    alternateName: BUSINESS.legalName,
    url: siteUrl,
    logo: `${siteUrl}/branding/web_header_full_transparent_180h.png`,
    image: `${siteUrl}/branding/social_profile_1200_white_bg.png`,
    telephone: BUSINESS.telephone,
    email: BUSINESS.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: BUSINESS.streetAddress,
      addressLocality: BUSINESS.locality,
      addressRegion: BUSINESS.region,
      postalCode: BUSINESS.postalCode,
      addressCountry: BUSINESS.country,
    },
    areaServed: BUSINESS.areaServed.map((a) => ({ "@type": "City", name: a })),
  };
  if (stats?.average_rating && stats?.review_count) {
    base.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: stats.average_rating,
      reviewCount: stats.review_count,
    };
  }
  return base;
}

function faqSchema(faqs) {
  if (!faqs || !faqs.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}

function reviewListSchema(reviews) {
  if (!reviews || !reviews.length) return null;
  return reviews.map((r) => ({
    "@context": "https://schema.org",
    "@type": "Review",
    reviewRating: {
      "@type": "Rating",
      ratingValue: r.rating || 5,
      bestRating: 5,
    },
    author: { "@type": "Person", name: r.name || "Anonymous" },
    reviewBody: r.quote,
    itemReviewed: {
      "@type": "Organization",
      name: BUSINESS.name,
    },
  }));
}

function courseSchema(program, siteUrl) {
  if (!program) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    name: program.name,
    description: program.short_description || program.description || "",
    provider: {
      "@type": "Organization",
      name: BUSINESS.name,
      sameAs: siteUrl,
    },
    audience: {
      "@type": "EducationalAudience",
      educationalRole: "student",
      audienceType: program.age_range || program.ages || "children",
    },
    courseMode: program.format || "in person",
  };
}

export default function SEO({
  title = "Nexora Learning & Development Centre | Play-Based Behaviour Support for Children",
  description = "Nexora Learning & Development Centre — STEM engineering, robotics, brick-based building, summer camps, and play-based support for children in Richmond BC.",
  image,
  path = "/",
  schemas = [],
  faqs,
  reviews,
  reviewStats,
  program,
  includeOrganization = true,
  noindex = false,
}) {
  const fullTitle = title.includes("Nexora") ? title : `${title} · Nexora`;
  const siteUrl =
    process.env.REACT_APP_SITE_URL ||
    (typeof window !== "undefined" ? window.location.origin : "https://www.nexoracentre.ca");
  const url = `${siteUrl}${path}`;

  const all = [];
  if (includeOrganization) all.push(localBusinessSchema(siteUrl, reviewStats));
  const fq = faqSchema(faqs);
  if (fq) all.push(fq);
  const rv = reviewListSchema(reviews);
  if (rv) all.push(...rv);
  const cs = courseSchema(program, siteUrl);
  if (cs) all.push(cs);
  if (Array.isArray(schemas)) all.push(...schemas.filter(Boolean));

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      {/* Default social preview: Nexora brand image */}
      <meta property="og:image" content={image || `${siteUrl}/branding/social_profile_1200_white_bg.png`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image || `${siteUrl}/branding/social_profile_1200_white_bg.png`} />
      {all.map((obj, idx) => (
        <script
          key={idx}
          type="application/ld+json"
          // Safe: safeJsonLd strictly serializes generated objects and escapes </>&
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: safeJsonLd(obj) }}
        />
      ))}
    </Helmet>
  );
}
